import { Link } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Button } from '@mantine/core';
import { IconChevronRight, IconHistory } from '@tabler/icons-react';
import { PROCEDURES_RUN_STATUS, useProceduresMemberService, type ProcedureRunListItem } from '@actschurch/ccms-sdk';
import { EmptyState, ErrorState, LoadingState, PageIntro, StatusPill, type StatusTone } from '@/components/ui';
import { formatDuration, formatStamp, plural } from '@/lib/format';
import { proceduresKeys } from '@/lib/queryKeys';

const PAGE_SIZE = 25;

function tone(statusID: number): StatusTone {
  if (statusID === PROCEDURES_RUN_STATUS.Completed) return 'ok';
  if (statusID === PROCEDURES_RUN_STATUS.InProgress) return 'info';
  return 'neutral';
}

function RunRow({ run }: { run: ProcedureRunListItem }) {
  const inProgress = run.statusID === PROCEDURES_RUN_STATUS.InProgress;
  const to = inProgress
    ? `/p/${encodeURIComponent(run.code)}/run/${run.runID}`
    : `/p/${encodeURIComponent(run.code)}/done/${run.runID}`;
  return (
    <Link to={to} className="proc-runrow">
      <span className="txt">
        <span className="t">{run.procedureTitle}</span>
        <span className="d">
          {formatStamp(run.startedAt)}
          {run.durationSeconds ? ` · ${formatDuration(run.durationSeconds)}` : ''}
        </span>
        <span className="meta">
          <StatusPill tone={tone(run.statusID)}>{run.statusName}</StatusPill>
          <span>{run.stepsDone} of {plural(run.stepsTotal, 'step')}</span>
          {run.problemCount > 0 && <StatusPill tone="bad" dot={false}>{plural(run.problemCount, 'problem')}</StatusPill>}
        </span>
      </span>
      <span className="chev" aria-hidden="true"><IconChevronRight size={22} /></span>
    </Link>
  );
}

/** /me/runs: the signed-in person's runs, newest first. Unfinished runs open the runner. */
export default function MyRunsPage() {
  const member = useProceduresMemberService();
  const query = useInfiniteQuery({
    queryKey: proceduresKeys.myRuns(),
    initialPageParam: 1,
    queryFn: ({ pageParam }) => member.getMyRuns({ page: pageParam, pageSize: PAGE_SIZE }),
    getNextPageParam: (last) => (last.page < last.totalPages ? last.page + 1 : undefined),
  });
  const runs = (query.data?.pages ?? []).flatMap((p) => p.items);

  return (
    <div className="proc-main">
      <PageIntro title="My runs" lede="Every procedure you've run while signed in." />
      {query.isLoading && <LoadingState label="Loading your runs" count={3} />}
      {query.isError && <ErrorState error={query.error} onRetry={() => void query.refetch()} retrying={query.isFetching} />}
      {query.isSuccess && runs.length === 0 && (
        <EmptyState icon={<IconHistory size={28} />} title="No runs yet" action={<Button component={Link} to="/" fullWidth>Find a procedure</Button>}>
          When you run a procedure while signed in, it shows here.
        </EmptyState>
      )}
      {runs.length > 0 && (
        <div className="proc-stack">
          {runs.map((r) => <RunRow key={r.runID} run={r} />)}
        </div>
      )}
      {query.hasNextPage && (
        <Button variant="default" fullWidth mt="md" loading={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>
          Show more
        </Button>
      )}
    </div>
  );
}
